/*

Verto FreeSWITCH interface

Copyright (c) 2019 Roman Yerin <r.yerin@640kb.co.uk>

All rights reserved.

Redistribution and use in source and binary forms, with or without
modification, are permitted provided that the following conditions are met:
    * Redistributions of source code must retain the above copyright
      notice, this list of conditions and the following disclaimer.
    * Redistributions in binary form must reproduce the above copyright
      notice, this list of conditions and the following disclaimer in the
      documentation and/or other materials provided with the distribution.
    * Neither the name of the <organization> nor the
      names of its contributors may be used to endorse or promote products
      derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL <COPYRIGHT HOLDER> BE LIABLE FOR ANY
DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.

*/

import { generateGUID, VertoBase } from './base'
import { JsonRpcClient, JsonRpcClientParams } from './json-rpc'
import { CallDirection, VertoRtc } from './rtc'

interface VertoOptions {
  transportConfig: JsonRpcClientParams
  rtcConfig: RTCConfiguration
  debug?: boolean
  ice_timeout?: number
}

interface VertoCallOptions {
  caller_id_number?: string
  caller_id_name?: string
  callee_id_number?: string
  callee_id_name?: string
}

const VertoMessage = {
  Invite: 'verto.invite',
  Answer: 'verto.answer',
  Bye: 'verto.bye',
  Media: 'verto.media',
  Attach: 'verto.attach',
  Modify: 'verto.modify',
  Subscribe: 'verto.subscribe',
  Unsubscribe: 'verto.unsubscribe',
  Info: 'verto.info',
  Display: 'verto.display',
  ClientReady: 'verto.clientReady',
  Broadcast: 'verto.broadcast',
  Ping: 'verto.ping',
}

const eSMessage = {
  Ping: 'eS.heartbeat',
  StartRecord: 'eS.recordStart',
  StopRecord: 'eS.recordStop',
}

type TimeoutID = ReturnType<typeof setTimeout>

class VertoCall extends VertoBase {
  private rtc: VertoRtc | undefined

  private rpc: JsonRpcClient | undefined

  public id: string

  public options: VertoCallOptions

  public direction: CallDirection = 0

  public holdStatus: boolean = false

  private intervalId: TimeoutID | null = null

  constructor(
    conf: RTCConfiguration,
    rpc: JsonRpcClient,
    dest?: string,
    id?: string,
    options?: VertoCallOptions,
    ice_timeout?: number,
    debug?: boolean,
    private pingInterval = 3000,
  ) {
    super(debug)
    this.id = id || generateGUID()
    this.options = options || <VertoCallOptions>{}
    this.direction = dest ? CallDirection.Outgoing : CallDirection.Incoming
    this.rtc = new VertoRtc(conf, this.direction, ice_timeout, debug)
    this.rpc = rpc

    this.rtc.subscribeEvent('send-offer', (sessionDescription) => {
      let dialogParams = { ...this.options }
      dialogParams = Object.assign(dialogParams, { destination_number: dest, callID: this.id })
      this.invite(dialogParams, sessionDescription)
    })

    this.rtc.subscribeEvent('send-answer', (sessionDescription) => {
      this.rpc?.call(
        VertoMessage.Answer,
        { dialogParams: { destination_number: dest, callID: this.id }, sdp: sessionDescription.sdp },
        () => {},
        (error) => {
          this.dispatchEvent('error', error)
        },
      )
    })

    this.rtc.subscribeEvent('track', (track) => {
      this.dispatchEvent('track', track)
    })
  }

  inviteTimeout: TimeoutID | null = null

  clearInviteTimeout() {
    if (this.inviteTimeout) {
      clearTimeout(this.inviteTimeout)
      this.inviteTimeout = null
    }
  }

  invite(dialogParams: Record<string, unknown>, sessionDescription: RTCSessionDescriptionInit) {
    this.clearInviteTimeout()

    this.rpc?.call(
      VertoMessage.Invite,
      { dialogParams, sdp: sessionDescription.sdp },
      () => {
        this.clearInviteTimeout()
        // start heartbeat in invite success callback
        this.intervalId = setInterval(() => {
          if (!this.rpc) {
            return
          }

          this.rpc.call(
            eSMessage.Ping,
            {
              dialogParams: {
                callID: this.id,
              },
            },
            () => {},
            (error) => {
              this.dispatchEvent('error', error)
            },
          )
          // also send verto.ping to keep track of websocket connection
          // FreeSWITCH will answer with PONG message: {...,"result":{"message":"PONG","sessid":"sessid"}}
          this.rpc.call(
            VertoMessage.Ping,
            {
              dialogParams: {
                callID: this.id,
              },
            },
            () => {
              // keep the connection alive as long as PONGs are received
              this.onPong()
            },
            (error) => {
              this.dispatchEvent('error', error)
            },
          )

          this.pongTimeout = setTimeout(() => {
            this.onPongTimeout()
          }, this.pingInterval * 3) // wait for 3 ping intervals
        }, this.pingInterval)
        this.onInitialized()
      },
      (error) => {
        this.clearInviteTimeout()
        this.dispatchEvent('error', error)
        this.clear()
      },
    )

    this.inviteTimeout = setTimeout(() => {
      this.clear()
    }, 10000) // wait 10 seconds before timing out the call
  }

  onInitialized() {
    this.dispatchEvent('initialized')
  }

  onAnswer(sdp: string) {
    if (sdp) {
      this.rtc?.onMedia(sdp)
    }
    this.dispatchEvent('answer')
  }

  onDisplay(sdp: string) {
    if (sdp) {
      this.rtc?.onMedia(sdp)
    }
    this.dispatchEvent('display')
  }

  onMedia(sdp: string) {
    this.rtc?.onMedia(sdp)
    this.dispatchEvent('media')
  }

  private pongTimeout: TimeoutID | null = null
  onPong() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout)
      this.pongTimeout = null
    }
    this.dispatchEvent('pong')
  }

  onPongTimeout() {
    this.dispatchEvent('pong-timeout')
    this.clear()
  }

  addTrack(track: MediaStreamTrack) {
    this.rtc?.addTrack(track)
  }

  preSdp(sdp: string) {
    this.rtc?.preSdp(sdp)
  }

  answer(tracks: Array<MediaStreamTrack>) {
    this.rtc?.answer(tracks)
  }

  hangupTimeout: TimeoutID | null = null

  clearHangupTimeout() {
    if (this.hangupTimeout) {
      clearTimeout(this.hangupTimeout)
      this.hangupTimeout = null
    }
  }

  hangup(params = {}) {
    this.clearHangupTimeout()
    this.dispatchEvent('hangup')
    this.rpc?.call(
      VertoMessage.Bye,
      { dialogParams: { callID: this.id }, ...params },
      () => {
        this.clear()
      },
      () => {
        this.clear()
      },
    )

    this.hangupTimeout = setTimeout(() => {
      this.clear()
    }, 5000) // wait 5 seconds before force clearing the call
  }

  clear() {
    this.dispatchEvent('bye', this)
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
    if (this.rtc) {
      this.rtc.close()
      this.rtc = undefined
    }
  }

  dtmf(input: string) {
    this.rpc?.call(
      VertoMessage.Info,
      { dtmf: input.toString(), dialogParams: { callID: this.id } },
      () => {},
      (error) => {
        this.dispatchEvent('error', error)
      },
    )
  }

  hold(params?: object) {
    this.rpc?.call(
      VertoMessage.Modify,
      { action: 'hold', dialogParams: { callID: this.id, ...this.options, ...params } },
      (data: { holdState: string }) => {
        if (data.holdState === 'held') {
          this.holdStatus = true
          this.dispatchEvent('hold', this)
        } else {
          this.holdStatus = false
          this.dispatchEvent('unhold', this)
        }
      },
      (error) => {
        this.dispatchEvent('error', error)
      },
    )
  }

  unhold(params?: object) {
    this.rpc?.call(
      VertoMessage.Modify,
      { action: 'unhold', dialogParams: { callID: this.id, ...this.options, ...params } },
      (data: { holdState: string }) => {
        if (data.holdState === 'held') {
          this.holdStatus = true
          this.dispatchEvent('hold', this)
        } else {
          this.holdStatus = false
          this.dispatchEvent('unhold', this)
        }
      },
      (error) => {
        this.dispatchEvent('error', error)
      },
    )
  }

  toggleHold(params?: object) {
    this.rpc?.call(
      VertoMessage.Modify,
      { action: 'toggleHold', dialogParams: { callID: this.id, ...this.options, ...params } },
      (data: { holdState: string }) => {
        if (data.holdState === 'held') {
          this.holdStatus = true
          this.dispatchEvent('hold', this)
        } else {
          this.holdStatus = false
          this.dispatchEvent('unhold', this)
        }
      },
      (error) => {
        this.dispatchEvent('error', error)
      },
    )
  }

  startRecording(params?: object) {
    this.rpc?.call(
      eSMessage.StartRecord,
      { dialogParams: { callID: this.id, ...this.options }, ...params },
      () => {
        this.dispatchEvent('record-started', this)
      },
      (error) => {
        this.dispatchEvent('error', error)
        this.dispatchEvent('record-error', this)
      },
    )
  }

  stopRecording(params?: object) {
    this.rpc?.call(
      eSMessage.StopRecord,
      { dialogParams: { callID: this.id, ...this.options }, ...params },
      () => {
        this.dispatchEvent('record-stopped', this)
      },
      (error) => {
        this.dispatchEvent('error', error)
        this.dispatchEvent('record-error', this)
      },
    )
  }
}

class Verto extends VertoBase {
  private vertoCall: VertoCall | null = null

  private rpc: JsonRpcClient | null

  private options: VertoOptions

  private logged_in: boolean = false

  constructor(options: VertoOptions) {
    //
    super(options.debug)
    this.options = options
    this.rpc = new JsonRpcClient(options.transportConfig, options.debug)
    this.rpc.setEventHandler((method, params) => {
      switch (method) {
        case VertoMessage.Answer: {
          const { callID } = params
          if (!this.vertoCall || this.vertoCall.id !== callID) {
            return
          }
          this.vertoCall.onAnswer(params.sdp || '')
          break
        }
        case VertoMessage.Display: {
          const { callID } = params
          if (!this.vertoCall || this.vertoCall.id !== callID) {
            return
          }
          this.vertoCall.onDisplay(params.sdp || '')
          break
        }
        case VertoMessage.Media: {
          const { callID } = params
          if (!this.vertoCall || this.vertoCall.id !== callID) {
            return
          }
          this.vertoCall.onMedia(params.sdp || '')
          break
        }
        case VertoMessage.Invite: {
          this.vertoCall = new VertoCall(
            this.options.rtcConfig,
            this.rpc as any,
            '',
            params.callID,
            { caller_id_name: params.caller_id_name, caller_id_number: params.caller_id_number },
            this.options.ice_timeout,
            this.options.debug,
          )
          this.vertoCall.preSdp(params.sdp || '')
          this.dispatchEvent('invite', this.vertoCall)
          break
        }
        case VertoMessage.Bye: {
          // when call is ended by remote party
          const { callID } = params
          if (!this.vertoCall || this.vertoCall.id !== callID) {
            return
          }
          this.vertoCall.clear()
          break
        }
        default:
          break
      }
    })
    this.rpc.setReconnectHandler(() => {
      if (this.logged_in) this.login()
    })
    this.options.rtcConfig = { ...(this.options.rtcConfig || {}) } as any
  }

  login(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.rpc?.call(
        'login',
        { login: this.options.transportConfig.login, passwd: this.options.transportConfig.passwd },
        (data: { sessid: string }) => {
          this.logged_in = true
          resolve(data)
        },
        (data) => {
          reject(data)
        },
      )
    })
  }

  call(tracks: Array<MediaStreamTrack>, destination: string, options?: VertoCallOptions): VertoCall {
    this.vertoCall = new VertoCall(
      this.options.rtcConfig as any,
      this.rpc as any,
      destination,
      generateGUID(),
      options,
      this.options.ice_timeout,
      this.options.debug,
    )

    this.vertoCall.subscribeEvent('bye', () => {
      // when call is ended by local party, bye is dispatched by calling VertoCall.clear()
      this.vertoCall = null
    })

    for (const track of tracks) this.vertoCall.addTrack(track)
    return this.vertoCall
  }

  isLogged(): boolean {
    return this.logged_in
  }

  logout() {
    this.vertoCall?.clear()
    this.vertoCall = null
    this.rpc?.close()
    this.rpc = null
    this.logged_in = false
  }
}

export { CallDirection, Verto, VertoCall }
